/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import msg from '@salesforce/retail-react-app/app/components/display-price/messages'

// Custom Contexts
import {useConversionRate} from '../../custom-contexts'

// Custom hooks
import useShipToCountry from '../../hooks/use-ship-to-country'

// utils
import {formatPrice} from '../../utils/price-utils'
/**
 * Component that displays list price of a product with a11y
 * @param currency - currency
 * @param price - price of the product
 * @param as - an HTML tag or component to be rendered as
 * @param isRange - show price as range or not
 * @param labelForA11y - label to be used for a11y
 * @param extraProps - extra props to be passed into Text Component
 * @returns {JSX.Element}
 */
const ListPrice = ({
    labelForA11y,
    price,
    isRange = false,
    as = 's',
    currency,
    conversionsRate,
    shipToCountryCode,
    ...extraProps
}) => {
    const contextConversionsRate = useConversionRate()
    const {shipToCountry: contextShipToCountry} = useShipToCountry()
    const intl = useIntl()

    const priceConversionsRate = conversionsRate || contextConversionsRate
    const shipToCountryId = shipToCountryCode || contextShipToCountry?.id

    const listPriceText = formatPrice(price, currency, priceConversionsRate, shipToCountryId, intl)

    return (
        <>
            {isRange ? (
                <Text
                    as={as}
                    color="darkGray"
                    aria-label={intl.formatMessage(msg.ariaLabelListPriceWithRange, {
                        listPrice: listPriceText || ''
                    })}
                    {...extraProps}
                >
                    {listPriceText}
                </Text>
            ) : (
                <Text
                    as={as}
                    color="darkGray"
                    aria-label={intl.formatMessage(msg.ariaLabelListPrice, {
                        listPrice: listPriceText || ''
                    })}
                    {...extraProps}
                >
                    {listPriceText}
                </Text>
            )}
        </>
    )
}

ListPrice.propTypes = {
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    as: PropTypes.string,
    isRange: PropTypes.bool,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    extraProps: PropTypes.object
}

export default ListPrice
